import { useMediaQuery } from '@mui/material';
import { Button } from 'flowbite-react';
import React, { useEffect } from 'react';
import useDisclosure from 'src/hooks/utils/useDisclosure';
import LinkedInImport from '../profile/LinkedInImport';
import { BsLinkedin } from 'react-icons/bs';
import { BaseAPIResponse, CreateApplicationInput, UIExtendedApplication } from 'types';
import ProfileUploadResume from '../profile/ProfileUploadResume';
import { useGetUserProfile } from 'src/hooks/profile.hooks';
import { useUserContext } from 'src/context/UserProvider';
import { UseMutationResult } from 'react-query';
import { useNavigate } from 'react-router-dom';
import { Drawer } from '../shared/Drawer';
import { HiCheckCircle } from 'react-icons/hi';
import classNames from 'classnames';

interface Props {
	isOpen?: boolean;
	onClose?: () => void;
	positionId?: string;
	isProfilePage?: boolean;
	setIsLinkedInModalOpen?: (arg: boolean) => void;
	createApplicationLoading: boolean;
	createApplicationMutation: UseMutationResult<BaseAPIResponse, Error, CreateApplicationInput>;
	application?: UIExtendedApplication;
	showLinkedInButton?: boolean;
}

const LinkedInResumeModal = ({
	isOpen,
	onClose,
	setIsLinkedInModalOpen,
	createApplicationLoading,
	createApplicationMutation,
	application,
	positionId,
	isProfilePage = false,
	showLinkedInButton,
}: Props) => {
	const matches = useMediaQuery('(max-width:600px)');

	const toggleDrawer = (newOpen: boolean) => () => {
		if (!newOpen && onClose) onClose();
	};

	return (
		<div className="isolate">
			<Drawer
				isModal={!matches}
				modalMaxWidth="414px"
				minHeight="fit-content"
				isOpen={isOpen ?? false}
				onClose={toggleDrawer(false)}
				header={<></>}
				body={
					<LinkedInImportModalContent
						positionId={positionId}
						isProfilePage={isProfilePage}
						application={application}
						createApplicationLoading={createApplicationLoading}
						createApplicationMutation={createApplicationMutation}
						onClose={onClose}
						showLinkedInButton={showLinkedInButton}
					/>
				}
			/>
		</div>
	);
};

interface ModalContentProps extends Props {}

const LinkedInImportModalContent = ({
	onClose,
	positionId,
	application,
	isProfilePage = false,
	showLinkedInButton,
	createApplicationLoading,
	createApplicationMutation,
}: ModalContentProps) => {
	const { isOpen: isOpenLinkedInModal, onOpen: onOpenLinkedInModal, onClose: onCloseLinkedInModal } = useDisclosure();
	const { user } = useUserContext();
	const { data: userProfile } = useGetUserProfile(user?.handler ?? '');
	const resumeLink = userProfile?.userDetail?.profile?.resumeLink;
	// resume name after last slash in url
	const resumeName = resumeLink?.substring(resumeLink.lastIndexOf('/') + 1);
	const navigate = useNavigate();
	const onLinkedinProfileImport = (linkedInUrl: string) => {
		// navigate(`/profile/${user?.handler}?selected=Profile`);
		navigate(`/profile/linkedin-edit?position=${positionId || application?.position?._id}`);
	};

	useEffect(() => {
		if (resumeLink && userProfile?.userDetail?.profile?.linkedInProfile) {
			onClose && onClose();
		}
	}, [resumeLink, userProfile?.userDetail?.profile?.linkedInProfile]);

	return (
		<>
			<div className="relative dark:bg-[#1F2A37] bg-inherit px-[16px] py-[24px] rounded-t-[16px]">
				<p className="text-[18px] font-[600] leading-[28px] dark:text-[#FAFAFC]">Update your details for applying</p>
				<p className="mt-[20px] text-[14px] font-[500] leading-[20px] dark:text-[#E4E4EB]">
					If you wish to interview for this job, please update your profile details.
				</p>
				{resumeLink ? (
					<div className="my-[16px] flex items-center justify-between">
						<p className="py-[6px] px-[10px] rounded-[24px] dark:bg-[#18222D] bg-[#b6b4b48a] dark:text-[#fff] flex items-center">
							{resumeName}
						</p>
						{user?.flags?.isMasaiStudent ? null : (
							<ProfileUploadResume
								customCta={
									<button className="dark:!text-[#CDF] text-[12px] font-[400] leading-[16px]">Upload new resume</button>
								}
							/>
						)}
						{/* <button className="!text-[#CDF] text-[12px] font-[400] leading-[16px]">Upload new resume</button> */}
					</div>
				) : null}
				<div className={classNames('flex justify-center gap-[16px]', resumeLink ? 'mt-0' : 'mt-[36px]')}>
					{showLinkedInButton ? (
						<Button
							className="text-white !text-sm font-inter !bg-[#7777E7] text-[14px] font-[500] leading-[20px] [&>span]:!px-[8px]"
							size="md"
							onClick={() => {
								onOpenLinkedInModal();
							}}
						>
							<span className="rounded-[1.5px] mr-[8px] flex items-center justify-center text-center">
								<BsLinkedin className="m-auto w-[16px] h-[16px]" color="white" />
							</span>
							LinkedIn Import
						</Button>
					) : null}
					{resumeLink ? (
						userProfile?.userDetail?.profile?.linkedInProfile && positionId ? (
							<div className="w-full md:max-w-[300px]">
								<Button
									color={'blue'}
									onClick={() => {
										if (
											userProfile?.userDetail?.profile?.linkedInProfile &&
											userProfile?.userDetail?.profile?.resumeLink
										) {
											onClose && onClose();
											createApplicationMutation.mutate({
												createInfo: {
													position: application?.position?._id.toString() || positionId || '',
													user: application?.user?._id || userProfile?.userDetail?._id || '',
												},
											});
										}
									}}
									className="w-full md:max-w-[300px] text-white !text-sm !font-medium font-inter bg-[#18222D] border !border-[#7777E7] hover:!bg-[#1f2a37] "
								>
									Apply
								</Button>
							</div>
						) : null
					) : (
						<ProfileUploadResume />
					)}
				</div>
			</div>
			{showLinkedInButton ? (
				<LinkedInImport
					isOpen={isOpenLinkedInModal}
					onClose={onCloseLinkedInModal}
					onProceed={onLinkedinProfileImport}
				/>
			) : null}
		</>
	);
};

export default LinkedInResumeModal;
